import { Button } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { ErrorText, SelectInput, TextInput } from './content-fields'
import type { ContentOption, TextFormValuesView, Translate } from './content-types'
import { DraftEditorView } from './draft-editor.view'

/** The "Other" source-language choice; its typed tag is `otherLocale`. */
export const OTHER_LOCALE_OPTION = '__other'

/**
 * Props for {@link TextFormView}.
 *
 * @property locales - Source-language options (the "Tenant default" and "Other" entries are added here).
 * @property errors - Translated messages by field.
 */
export interface TextFormViewProps {
	t: Translate
	values: TextFormValuesView
	types: ContentOption[]
	locales: string[]
	errors: Record<string, string>
	formError: string | null
	isBusy: boolean
	onChange: (field: keyof TextFormValuesView, value: string) => void
	onSubmit: () => void
}

function SourceLocaleFields({ props }: { props: TextFormViewProps }): ReactElement {
	const { t, values, errors, onChange } = props
	return (
		<>
			<SelectInput
				label={t('form.sourceLocale')}
				value={values.sourceLocale}
				emptyLabel={t('form.tenantDefault')}
				options={[
					...props.locales.map((l) => ({ id: l, label: l })),
					{ id: OTHER_LOCALE_OPTION, label: t('form.otherLocale') },
				]}
				error={values.sourceLocale === OTHER_LOCALE_OPTION ? undefined : errors.sourceLocale}
				onChange={(v) => onChange('sourceLocale', v)}
			/>
			{values.sourceLocale === OTHER_LOCALE_OPTION ? (
				<TextInput
					label={t('form.otherLocaleTag')}
					value={values.otherLocale}
					error={errors.sourceLocale}
					onChange={(v) => onChange('otherLocale', v)}
				/>
			) : null}
		</>
	)
}

/** The create-text form: type, code, name, description, source language, and the first draft (organism). */
export function TextFormView(props: TextFormViewProps): ReactElement {
	const { t, values, errors, onChange } = props
	return (
		<section className="flex flex-col gap-4">
			<h1 className="text-ds-xl font-semibold text-ds-text">{t('form.title')}</h1>
			<ErrorText message={props.formError} />
			<SelectInput
				label={t('form.type')}
				value={values.typeCode}
				emptyLabel={t('form.choose')}
				options={props.types}
				error={errors.typeCode}
				onChange={(v) => onChange('typeCode', v)}
			/>
			<TextInput label={t('form.code')} value={values.code} error={errors.code} onChange={(v) => onChange('code', v)} />
			<TextInput label={t('form.name')} value={values.name} error={errors.name} onChange={(v) => onChange('name', v)} />
			<TextInput
				label={t('form.description')}
				value={values.description}
				error={errors.description}
				onChange={(v) => onChange('description', v)}
			/>
			<SourceLocaleFields props={props} />
			<DraftEditorView
				t={t}
				title={values.title}
				body={values.body}
				errors={errors}
				onChange={(field, v) => onChange(field, v)}
			/>
			<div>
				<Button isDisabled={props.isBusy} onPress={props.onSubmit}>
					{t('form.create')}
				</Button>
			</div>
		</section>
	)
}
